/**
 * section-main-product.js
 *
 * Custom-element and event wiring for Mekar-specific PDP additions:
 *   - <urgency-viewers> : renders a randomized "X people viewing" counter
 *     that ticks +/-1 at a random interval within the configured range.
 *     Respects prefers-reduced-motion (initial value only, no ticking).
 *   - [data-side-badge]  : dispatches `mekar:open-welcome-popup` (brief 18)
 *   - [data-bloom-rewards] without href : opens minimal "coming soon" alert
 *   - .product__sticky-buy-bar price    : kept in sync with variant changes
 *     by reacting to Dawn's `variant:change` events emitted by product-info.
 *
 * No framework, no jQuery.
 */

(function () {
  'use strict';

  if (!window.customElements || customElements.get('urgency-viewers')) return;

  class UrgencyViewers extends HTMLElement {
    constructor() {
      super();
      this._timer = null;
    }

    connectedCallback() {
      const min = parseInt(this.dataset.min, 10) || 30;
      const max = parseInt(this.dataset.max, 10) || 80;
      const tickMin = parseInt(this.dataset.tickMin, 10) || 15;
      const tickMax = parseInt(this.dataset.tickMax, 10) || 30;

      this._min = Math.min(min, max);
      this._max = Math.max(min, max);
      this._tickMin = Math.max(1, Math.min(tickMin, tickMax));
      this._tickMax = Math.max(1, Math.max(tickMin, tickMax));

      this._countEl = this.querySelector('[data-viewers-count]');
      if (!this._countEl) return;

      this._value = this._randomBetween(this._min, this._max);
      this._render();

      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!reduced) this._scheduleTick();
    }

    disconnectedCallback() {
      if (this._timer) {
        clearTimeout(this._timer);
        this._timer = null;
      }
    }

    _randomBetween(lo, hi) {
      return Math.floor(Math.random() * (hi - lo + 1)) + lo;
    }

    _render() {
      this._countEl.textContent = String(this._value);
    }

    _scheduleTick() {
      const seconds = this._randomBetween(this._tickMin, this._tickMax);
      this._timer = setTimeout(() => this._tick(), seconds * 1000);
    }

    _tick() {
      const delta = Math.random() < 0.5 ? -1 : 1;
      let next = this._value + delta;
      if (next < this._min) next = this._min + 1;
      if (next > this._max) next = this._max - 1;
      this._value = next;
      this._render();
      this._scheduleTick();
    }
  }

  customElements.define('urgency-viewers', UrgencyViewers);

  // Side-badge 10% OFF => dispatch custom event for brief 18's popup listener
  document.addEventListener('click', function (event) {
    const badge = event.target.closest('[data-side-badge]');
    if (!badge) return;
    event.preventDefault();
    document.dispatchEvent(
      new CustomEvent('mekar:open-welcome-popup', { bubbles: true })
    );
  });

  // BloomRewards placeholder: when no href set, show a polite "coming soon"
  document.addEventListener('click', function (event) {
    const btn = event.target.closest('button[data-bloom-rewards]');
    if (!btn) return;
    event.preventDefault();
    const msg = btn.getAttribute('data-coming-soon-message') ||
      'BloomRewards is coming soon. Check back after our loyalty launch.';
    window.alert(msg);
  });

  // Keep sticky-buy-bar price in sync when variant changes.
  // Dawn's product-info.js dispatches `variant:change` on the <product-info> element
  // with detail { variant }.
  document.addEventListener('variant:change', function (event) {
    const productInfo = event.target;
    if (!productInfo) return;
    const bar = productInfo.querySelector('.product__sticky-buy-bar');
    if (!bar) return;
    const priceEl = bar.querySelector('[data-sticky-price]');
    const detail = event.detail || {};
    const variant = detail.variant || detail.selectedVariant;
    if (priceEl && variant && typeof variant.price !== 'undefined') {
      priceEl.textContent = formatMoney(variant.price);
    }
    const button = bar.querySelector('.product__sticky-buy-bar-button');
    if (button) {
      if (variant && variant.available === false) {
        button.setAttribute('disabled', 'disabled');
      } else {
        button.removeAttribute('disabled');
      }
    }
  });

  // Sticky bar intentionally shows "RM320.00" without the MYR suffix, even
  // though the shop's money_format includes it elsewhere. Fixed format on purpose.
  function formatMoney(cents) {
    if (typeof cents !== 'number') return String(cents);
    return 'RM' + (cents / 100).toFixed(2);
  }
})();
